import { postContentClass } from "@repo/fika-blog/src/classes"
import { postClass, postContainerClass } from "@repo/fika-blog/src/content.css"
import {
  detectLanguage,
  highlightHtml,
} from "@repo/fika-blog/src/lib/highlight"
import type { AccentHue } from "@repo/fika-design-system/color"
import { colorize } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import { dateToString } from "@repo/fika-shared/src/date.js"
import {
  KeywordsAndSummarySchema,
  keywordsAndSummaryJsonSchema,
  keywordsAndSummaryPrompt,
} from "@repo/fika-shared/src/keywords-and-summary.js"
import type { Bookmark } from "@repo/fika-shared/src/loader/bookmark-schema.js"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import type { Publisher } from "@repo/fika-shared/src/loader/publisher-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import { useNavigate } from "@solidjs/router"
import { format, intlFormatDistance } from "date-fns"
import DOMPurify from "dompurify"
import { marked } from "marked"
import {
  createMemo,
  createResource,
  createSignal,
  type JSXElement,
  Match,
  Show,
  Switch,
} from "solid-js"
import { ulid } from "ulid"
import z from "zod"
import { useAppContext } from "@/context/app"
import { fetchFromHandinger } from "@/data/fetchFromHandinger"
import { findOrCreate } from "@/data/storage"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Logo } from "@/designSystem/components/logo"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { Card } from "@/designSystem/patterns/card"
import { Navigation } from "@/designSystem/patterns/navigation"
import { TagWithSuggestions } from "@/designSystem/patterns/tags-with-suggestions"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/defaultJsonContent"
import { formatDate } from "@/lib/formatDate"
import { getTitle } from "@/lib/getTitle"
import { createBlogRouter } from "@/routes/posts-router"
import { createPublishersRouter } from "@/routes/publishers-router"
import { createStoriesRouter } from "@/routes/stories-router"
import {
  boxClass,
  ellipsisClass,
  placeholderRecipe,
  placeholderTextClass,
  publisherClass,
  publisherLinkClass,
  showMoreClass,
  storyContainerClass,
  subtitleClass,
  titleBoxClass,
  titleContainerClass,
  titleIconClass,
  titleTextClass,
} from "./frame.css"

const MAX_SUMMARY_LENGTH = 500
const MIN_SUMMARY_LENGTH = 200

type Metadata = {
  dc_title?: string | null
  dc_description?: string | null
  dc_created_at?: string | null
  feed_url?: string | null
  tags: string | null
  summary: string | null
}

type Props = {
  story: Story
}
export function Frame(props: Props) {
  const { LL } = useI18nContext()
  const { paths: storiesPaths } = createStoriesRouter()
  const { db } = useAppContext()
  const title = createMemo(() => getTitle(props.story.dc_title))

  const getPost = createMemo(() => {
    const bookmark = db.bookmark.findBy({ story_id: props.story.id })
    if (!bookmark) return null

    return db.post.findBy({ bookmark_id: bookmark?.id })
  })

  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.bookmarks(),
        href: storiesPaths().url,
      } as const,
    ]
  })

  return (
    <MainLayout
      header={<Navigation hue="emerald" buttons={buttons()} title={title()} />}
      footer={<StoryFooter story={props.story} title={title()} />}
    >
      <Box gap="12" fill>
        <Show when={getPost()}>{post => <PostCard post={post()} />}</Show>
        <StoryDetail story={props.story} />
      </Box>
    </MainLayout>
  )
}

type PlaceholderProps = {
  iconName: IconName
  hue: AccentHue
  text: string
}
function Placeholder(props: PlaceholderProps) {
  return (
    <div class={placeholderRecipe({ hue: props.hue })}>
      <Icon
        name={props.iconName}
        color={colorize(props.hue, "600")}
        size="24"
      />
      <span class={placeholderTextClass}>{props.text}</span>
    </div>
  )
}

type StoryFooterProps = {
  story: Story
  title: string
}
export function StoryFooter(props: StoryFooterProps) {
  const { db, session, replicache } = useAppContext()
  const navigate = useNavigate()
  const { LL } = useI18nContext()

  const { paths: blogPaths } = createBlogRouter()

  const getBookmark = createMemo(() =>
    db.bookmark.findBy({ story_id: props.story.id }),
  )

  const getPost = createMemo(() => {
    const bookmark = getBookmark()
    if (!bookmark) return null

    return db.post.findBy({ bookmark_id: bookmark?.id })
  })

  async function onCreatePost(bookmark: Bookmark) {
    const id = ulid()

    await replicache.mutate.createPost({
      id,
      bookmark_id: bookmark.id,
      title: props.title,
      json: defaultJsonContent,
    })

    navigate(blogPaths({ filter: "draft" }).post({ id, title: "untitled" }).url)
  }

  return (
    <ButtonDrawer
      hue="emerald"
      leftActions={
        <LinkButton
          hue="emerald"
          kind="darkGhost"
          iconName="external-link"
          target="_blank"
          href={props.story.url}
        >
          {LL().common.visit()}
        </LinkButton>
      }
      rightActions={
        <Show
          when={getBookmark()}
          fallback={
            <Button
              hue="emerald"
              iconName="book-marked"
              onClick={() => {
                replicache.mutate.initBookmark({
                  id: ulid(),
                  story_id: props.story.id,
                  user_id: session.userID,
                  bookmarked_at: new Date().toISOString(),
                  bookmarked_iso: new Date().toISOString().split("T")[0]!,
                })
              }}
            >
              bookmark
            </Button>
          }
        >
          {bookmark => (
            <>
              <Button
                hue="emerald"
                kind="darkGhost"
                iconName="trash-2"
                onClick={async () => {
                  await replicache.mutate.deleteBookmark(bookmark().id)

                  navigate("/bookmarks/all")
                }}
              >
                remove
              </Button>
              <Show when={!getPost()}>
                <Button
                  hue="amber"
                  iconName="message-square-quote"
                  onClick={() => onCreatePost(bookmark())}
                >
                  write post
                </Button>
              </Show>
            </>
          )}
        </Show>
      }
    />
  )
}

type StoryDetailProps = {
  story: Story
}
export function StoryDetail(props: StoryDetailProps) {
  const { db, session } = useAppContext()

  const getBookmark = createMemo(() =>
    db.bookmark.findBy({ story_id: props.story.id }),
  )

  const dataSource = createMemo(() => ({
    id: props.story.id,
    metadated_at: props.story.metadated_at,
    url: props.story.url,
  }))

  const [data] = createResource(dataSource, async source => {
    const parser = new DOMParser()
    const [md, metadata] = await Promise.all([
      findOrCreate(source.id, source.url, session.jwt),
      fetchMetadataAndKeywordsAndSummary(
        source.metadated_at,
        source.url,
        session.jwt,
      ),
    ])
    const html = await marked.parse(md, { async: true })
    const dom = parser.parseFromString(html, "text/html")

    for (const el of dom.querySelectorAll("pre code")) {
      let language

      const langClass = el.className
      const code = el.textContent
      if (!code) continue

      const match = langClass?.match(/language-(\w+)/)

      if (match && match.length > 1) {
        language = match[1]?.toLowerCase()
      }
      language ||= detectLanguage({ code })
      const html = highlightHtml({ code, language })

      el.classList.add(`language-${language}`)
      el.innerHTML = html
    }

    return {
      html: dom.documentElement.outerHTML,
      metadata,
    }
  })

  const metadata = createMemo(() => data()?.metadata ?? props.story)
  const title = createMemo(() => getTitle(metadata().dc_title))
  const getPublisher = createMemo(() => {
    const feedUrl = metadata().feed_url
    let publisher

    if (feedUrl) {
      publisher = db.publisher.findBy({ url: feedUrl })
    }

    if (publisher) return publisher

    const entry = db.entry.findBy({ story_id: props.story.id })
    if (!entry) return null

    return db.publisher.get(entry.publisher_id)
  })
  const state = createMemo(() => {
    if (data.loading) return "loading"
    if (data.error) return "error"
    if (!data()?.html) return "error"

    return "success"
  })

  const getTags = createMemo(() => {
    const target = getBookmark()
    if (!target) return new Set([] as string[])

    return new Set(
      db.tag
        .filterBy({ target_id: target.id, target_type: "bookmark" })
        .map(t => t.tag),
    )
  })

  const tagOptions = createMemo(() => {
    const target = getBookmark()
    if (!target) return []
    return db.tag.filterBy({ target_type: "bookmark" }).map(t => t.tag)
  })

  const [showMore, setShowMore] = createSignal(false)
  const shortSummary = createMemo(() => {
    const summary = metadata().summary
    if (!summary) return null

    const sentences = summary
      .split(".")
      .filter(sentence => sentence.trim().length > 0)
    let result = ""

    for (const sentence of sentences) {
      const nextResult = result ? `${result}.${sentence}` : sentence

      if (nextResult.length < MIN_SUMMARY_LENGTH) {
        result = nextResult
        continue
      }

      if (nextResult.length > MAX_SUMMARY_LENGTH) {
        break
      }

      result = nextResult
      break
    }

    return `${result}.`
  })

  return (
    <Box gap="24" fill class={storyContainerClass}>
      <Box>
        <Title
          title={title()}
          iconName="book-marked"
          publisher={getPublisher()}
          story={props.story}
          bookmark={getBookmark()}
          metadata={metadata()}
        />
      </Box>
      <Show when={getBookmark()}>
        {bookmark => (
          <TagWithSuggestions
            options={tagOptions()}
            tags={getTags()}
            suggestions={metadata().tags}
            targetType="bookmark"
            targetId={bookmark().id}
            hue="emerald"
          />
        )}
      </Show>
      <Show when={shortSummary()}>
        {shortSummary => (
          <Box
            borderWidth="1"
            borderColor="emerald100"
            borderRadius="8"
            padding="12"
            background="emerald50"
          >
            <Text fontSize="m" color="emerald800" fontWeight="500">
              summary{" "}
              <Text fontSize="m" color="emerald800">
                — {showMore() ? metadata().summary : shortSummary()}
                <Show when={metadata().summary}>
                  {summary => (
                    <Show when={summary().length > shortSummary().length}>
                      <button
                        type="button"
                        onClick={() => setShowMore(!showMore())}
                        class={showMoreClass}
                      >
                        {showMore() ? "▲ show less" : "▼ show more"}
                      </button>
                    </Show>
                  )}
                </Show>
              </Text>
            </Text>
          </Box>
        )}
      </Show>
      <Box fill class={boxClass}>
        <Switch>
          <Match when={state() === "loading"}>
            <Placeholder
              hue="emerald"
              iconName="download-cloud"
              text="downloading the story"
            />
          </Match>
          <Match when={state() === "error"}>
            <Placeholder
              hue="rose"
              iconName="x-circle"
              text="we had a problem fetching this story, we've been notified and will fix it soon."
            />
          </Match>
          <Match when={state() === "success"}>
            <div class={postContainerClass}>
              <div class={postClass}>
                <div
                  class={postContentClass}
                  innerHTML={DOMPurify.sanitize(data()!.html)}
                />
              </div>
            </div>
          </Match>
        </Switch>
      </Box>
    </Box>
  )
}

type TitleProps = {
  title: string
  publisher: Publisher | null
  story: Story
  bookmark: Bookmark | null
  subtitle?: JSXElement
  iconName: IconName
  metadata: Metadata
}
function Title(props: TitleProps) {
  const createdAt = createMemo(() => props.metadata.dc_created_at)
  const bookmarkedAt = createMemo(() => props.bookmark?.bookmarked_at)
  const { paths } = createPublishersRouter()

  return (
    <Box class={titleContainerClass}>
      <Icon
        class={titleIconClass}
        name={props.iconName}
        size="32"
        color="emerald700"
      />
      <Box class={titleBoxClass} gap="16" alignItems="center">
        <Box gap="4" class={subtitleClass}>
          <h1 class={titleTextClass}>{props.title}</h1>
          <Text
            fontSize="s"
            color="sand700"
            lineClamp="1"
            textAlign="center"
            fontFamily="serif"
          >
            <Show when={createdAt()}>
              {date => (
                <>
                  {"published "}
                  <time title={format(date(), "PPp")}>
                    {formatDate(date())}
                  </time>
                </>
              )}
            </Show>
            <Show when={createdAt() && bookmarkedAt()}>{" • "}</Show>
            <Show when={bookmarkedAt()}>
              {date => (
                <>
                  {"bookmarked "}
                  <time title={format(date(), "PPp")}>
                    {intlFormatDistance(date(), new Date())}
                  </time>
                </>
              )}
            </Show>
          </Text>
        </Box>
        <Show when={props.publisher}>
          {publisher => (
            <Box
              alignItems="center"
              justifyContent="center"
              flexDirection="row"
              class={publisherClass}
            >
              <a
                href={paths().publisher(publisher()).url}
                class={publisherLinkClass}
              >
                <Logo
                  image={{
                    url: publisher().logo_url,
                    imageClass: "miniLogo",
                  }}
                  fallback={<Icon size="18" name="newspaper" />}
                  alt={getTitle(publisher().dc_title)}
                />
                <span class={ellipsisClass}>
                  {getTitle(publisher().dc_title)}
                </span>
              </a>
            </Box>
          )}
        </Show>
      </Box>
    </Box>
  )
}

function PostCard(props: { post: Post }) {
  const { paths: blogPaths } = createBlogRouter()
  const postDate = createMemo(
    () => props.post?.published_at || props.post?.created_at,
  )
  const humanReadableDate = createMemo(() => {
    const date = postDate()
    if (!date) return null
    const verb = props.post?.published_at ? "published" : "created"

    return `${verb} ${intlFormatDistance(date, new Date())}`
  })
  const formatDate = createMemo(() => {
    const date = postDate()
    if (!date) return undefined

    return format(date, "PPp")
  })

  return (
    <Box gap="24">
      <Box flexDirection="row" gap="24" justifyContent="flex-end">
        <Card
          hue="amber"
          href={blogPaths().post(props.post).url}
          logo={<Icon name="book-text" size="24" color="amber600" />}
          iconSize="18"
        >
          <Box>
            <Text color="amber900" fontWeight="500" tight>
              go to blogmark
            </Text>
            <Text fontSize="s" color="amber700" lineClamp="1">
              <time title={formatDate()} datetime={formatDate()}>
                {humanReadableDate()}
              </time>
            </Text>
          </Box>
        </Card>
      </Box>
    </Box>
  )
}

const LooseMetadataSchema = z.object({
  description: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  feed: z.string().nullable().optional(),
  date: z.date().nullable().optional(),
})

async function fetchMetadataAndKeywordsAndSummary(
  metadatedAt: string | null,
  url: string,
  jwt?: string,
): Promise<Metadata | null> {
  if (metadatedAt) return null
  if (!jwt) return null

  try {
    const [metadata, keywordsAndSummary] = await Promise.all([
      fetchFromHandinger({
        handler: "meta",
        jwt,
        params: {
          url,
          advanced_scraping: "true",
        },
      })
        .then(r => r.json())
        .then(r => LooseMetadataSchema.parse(r)),
      fetchFromHandinger({
        handler: "llm",
        jwt,
        params: {
          url,
          prompt: keywordsAndSummaryPrompt,
          json_schema: keywordsAndSummaryJsonSchema,
        },
      })
        .then(r => r.json())
        .then(r => KeywordsAndSummarySchema.parse(r)),
    ])

    return {
      tags: keywordsAndSummary?.tags?.join(","),
      summary: keywordsAndSummary?.summary,
      dc_title: metadata?.title,
      dc_description: metadata?.description,
      feed_url: metadata?.feed,
      dc_created_at: dateToString(metadata?.date),
    }
  } catch (error) {
    console.error(error)
    return null
  }
}
